import React, { useState, useEffect } from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { Fab, Tooltip } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';

/**
 * Componente de Helper Interativo para páginas
 * 
 * @param {Array} steps - Array de objetos com os passos do tour
 * @param {string} tourKey - Chave única para identificar o tour no localStorage
 * @param {boolean} autoStart - Se deve iniciar automaticamente na primeira visita
 * @param {boolean} showButton - Se deve mostrar o botão flutuante de ajuda
 */
export const PageHelper = ({
  steps = [],
  tourKey = 'page-tour',
  autoStart = true,
  showButton = true
}) => {
  const [runTour, setRunTour] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [brandColor, setBrandColor] = useState('var(--color-brand-live)');

  useEffect(() => {
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-primary').trim();
    if (color) setBrandColor(color);
  }, []);

  useEffect(() => {
    // Verifica se é a primeira visita
    const hasSeenTour = localStorage.getItem(`${tourKey}-completed`);
    
    if (!hasSeenTour && autoStart && steps.length > 0) {
      // Aguarda um pouco para a página carregar completamente e verificar se os elementos existem
      setTimeout(() => {
        // Verifica se pelo menos o primeiro elemento com data-tour existe
        const firstElement = steps.length > 1 && steps[1].target !== 'body' 
          ? document.querySelector(steps[1].target) 
          : true;
        
        if (firstElement) {
          setRunTour(true);
        } else {
          // Se não encontrou, tenta novamente após mais um delay
          setTimeout(() => {
            setRunTour(true);
          }, 1000);
        }
      }, 1200);
    }
  }, [tourKey, autoStart, steps.length, steps]);

  const handleJoyrideCallback = (data) => {
    const { action, index, status, type } = data;

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      // Avança para o próximo step
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    } else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Tour finalizado ou pulado
      setRunTour(false);
      setStepIndex(0);
      localStorage.setItem(`${tourKey}-completed`, 'true');
    }
  };

  const startTour = () => {
    setStepIndex(0);
    setRunTour(true);
  };

  return (
    <>
      <Joyride
        steps={steps}
        run={runTour}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        scrollOffset={120}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            arrowColor: 'var(--vo-white)',
            backgroundColor: 'var(--vo-white)',
            overlayColor: 'rgba(0, 0, 0, 0.6)',
            primaryColor: brandColor,
            textColor: 'var(--vo-ink-2)',
            width: 380,
            zIndex: 10000,
          },
          buttonNext: {
            backgroundColor: brandColor,
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            padding: '10px 20px',
          },
          buttonBack: {
            color: 'var(--vo-slate)',
            fontSize: 14,
            marginRight: 10,
          },
          buttonSkip: {
            color: 'var(--vo-steel)',
            fontSize: 13,
          },
          tooltip: {
            borderRadius: 12,
            padding: 20,
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
          },
          tooltipContainer: {
            textAlign: 'left',
          },
          tooltipTitle: {
            fontSize: 18,
            fontWeight: 600,
            marginBottom: 8,
            color: 'var(--vo-ink)',
          },
          tooltipContent: {
            fontSize: 14,
            lineHeight: 1.6,
            color: 'var(--vo-ink-2)',
          },
        }}
        locale={{
          back: 'Voltar',
          close: 'Fechar',
          last: 'Finalizar',
          next: 'Próximo',
          skip: 'Pular tour',
        }}
      />

      {showButton && (
        <Tooltip title="Iniciar tour guiado" placement="left">
          <Fab
            color="primary"
            aria-label="help"
            onClick={startTour}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 9999,
              background: 'var(--color-brand-live)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'var(--color-brand-live)',
                transform: 'scale(1.1)',
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)',
              },
            }}
          >
            <HelpOutline />
          </Fab>
        </Tooltip>
      )}
    </>
  );
};
